import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import CheckoutModal from '../components/CheckoutModal';
import ReviewModal from '../components/ReviewModal';
import WhatsAppConfirmModal from '../components/WhatsAppConfirmModal';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Menu = () => {
  const { isOpen: cartOpen, setIsOpen: setCartOpen } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [banner, setBanner] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'All');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [searchQuery] = useState(searchParams.get('search') || '');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/banners`),
      axios.get(`${API}/categories`),
      axios.get(`${API}/products?active=true`),
    ]).then(([bannerRes, catRes, prodRes]) => {
      setBanner(bannerRes.data.find(b => b.type === 'menu'));
      setCategories(catRes.data);
      setProducts(prodRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const categoryOrder = categories.map(c => c.name);
  const filtered = searchQuery
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : activeCategory === 'All' ? products : products.filter(p => p.category?.name === activeCategory);

  const grouped = {};
  filtered.forEach(p => {
    const catName = p.category?.name || 'Other';
    if (!grouped[catName]) grouped[catName] = [];
    grouped[catName].push(p);
  });
  const sortedGroups = Object.keys(grouped).sort((a, b) => {
    const ia = categoryOrder.indexOf(a);
    const ib = categoryOrder.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  return (
    <div className="min-h-screen bg-cream" data-testid="menu-page">
      <SEO
        title="Full Menu — Cakes, Pastries, Patties & More"
        description="Browse our full menu of handcrafted cakes, pastries, patties, pizzas, snacks and party items. Order online for delivery or pickup from Apni Dhani Bakery."
        path="/menu"
      />
      <Navbar onCartClick={() => setCartOpen(true)} />
      <PageBanner banner={banner} />

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Category filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-thin" data-testid="category-tabs">
          {['All', ...categoryOrder].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-[#7A1125] text-white'
                  : 'bg-white border border-[#ECE0DA] text-[#7A6A63] hover:border-[#7A1125]'
              }`}
              data-testid={`tab-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {searchQuery && (
          <p className="text-[#7A6A63] text-sm mb-6">
            Showing results for "<strong className="text-[#1F1410]">{searchQuery}</strong>"
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sortedGroups.length === 0 ? (
          <div className="text-center py-20 text-[#7A6A63]">
            No products found
          </div>
        ) : (
          <div className="space-y-12" data-testid="products-by-category">
            {sortedGroups.map(catName => (
              <div key={catName} id={`cat-${catName}`}>
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="font-playfair text-2xl font-bold text-[#1F1410]">{catName}</h2>
                  <div className="flex-1 h-px bg-[#ECE0DA]" />
                  <span className="text-xs text-[#7A6A63]">{grouped[catName].length} items</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {grouped[catName].map(p => (
                    <ProductCard key={p._id} product={p} data_testid={`menu-product-${p._id}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} />
      <CheckoutModal isOpen={checkoutOpen} onClose={() => setCheckoutOpen(false)} onOrderPlaced={(o) => { setCheckoutOpen(false); setPendingOrder(o); }} />
      {pendingOrder && <WhatsAppConfirmModal order={pendingOrder} onClose={() => { setPlacedOrder(pendingOrder); setPendingOrder(null); }} />}
      {placedOrder && <ReviewModal order={placedOrder} onClose={() => setPlacedOrder(null)} />}
    </div>
  );
};

export default Menu;
